// Globally smooths out image loading.
//
// Browsers paint partial bytes as they arrive (top-to-bottom scanlines for
// non-progressive PNGs/JPEGs, blurry-then-sharper for progressive JPEGs).
// That progressive paint is what reads as "the image is loading line by line"
// in the UI. CSS alone can't suppress it — the only fix is to keep the <img>
// hidden until the browser has fully decoded the resource and can paint the
// whole frame in one go.
//
// This plugin installs a MutationObserver at the app root that watches every
// <img> insertion (and src change). For each one, it:
//   1. Hides the element (opacity:0) with a short opacity transition.
//   2. Probes the URL in a separate Image() and awaits decode().
//   3. Once decode resolves, transitions opacity back to 1.
//
// Result: cold images fade in cleanly instead of streaming in scanlines.
// Cached images are detected via `img.complete` and rendered immediately
// with no fade — there's nothing to hide. Data/blob URIs are skipped for
// the same reason.
//
// Escape hatch: any <img data-no-smooth> is left alone.

const PROCESSED = 'smoothImage';
const TRANSITION = 'opacity 200ms ease';

function shouldSkip(img) {
    if (img.dataset[PROCESSED] === 'pending') return true;
    if (img.hasAttribute('data-no-smooth')) return true;
    // Vue often creates the element before setting src — the attribute
    // observer will catch us up when src lands.
    if (!img.src) return true;
    // Already in memory: no progressive paint to suppress.
    if (img.src.startsWith('data:') || img.src.startsWith('blob:')) return true;
    return false;
}

function applySmooth(img) {
    if (shouldSkip(img)) return;

    // Browser cache hit: render instantly, no fade.
    if (img.complete && img.naturalWidth > 0) {
        img.dataset[PROCESSED] = 'ready';
        return;
    }

    img.dataset[PROCESSED] = 'pending';

    // Preserve any inline opacity/transition the caller had set so we can
    // restore them if the src is swapped out mid-probe.
    const prevOpacity = img.style.opacity;
    const prevTransition = img.style.transition;

    img.style.opacity = '0';
    img.style.transition = TRANSITION;

    const url = img.src;
    const probe = new Image();
    probe.src = url;

    const reveal = () => {
        // Bail if the live element's src has been reassigned under us during
        // a fast Vue update — the new src will be probed on its own.
        if (img.src !== url) {
            img.style.opacity = prevOpacity;
            img.style.transition = prevTransition;
            delete img.dataset[PROCESSED];
            return;
        }
        img.style.opacity = '1';
        img.dataset[PROCESSED] = 'ready';
    };

    const fail = () => {
        // Surface the broken image (browser shows alt / broken-image icon)
        // rather than leaving an invisible element sitting in the layout.
        img.style.opacity = prevOpacity || '1';
        img.dataset[PROCESSED] = 'failed';
    };

    if (typeof probe.decode === 'function') {
        // decode() resolves only when the next paint can show the full
        // image — strictly stronger than `load`.
        probe.decode().then(reveal).catch(fail);
    } else {
        probe.onload = reveal;
        probe.onerror = fail;
    }
}

function scan(root) {
    if (!root) return;
    if (root.tagName === 'IMG') {
        applySmooth(root);
        return;
    }
    if (typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('img').forEach(applySmooth);
}

function startObserving() {
    scan(document.body);
    const observer = new MutationObserver(mutations => {
        for (const m of mutations) {
            if (m.type === 'childList') {
                m.addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) scan(node);
                });
                continue;
            }
            if (m.type === 'attributes' && m.target.tagName === 'IMG' && m.attributeName === 'src') {
                // src changed — reset the marker so applySmooth re-probes the
                // new URL. Without this, the old `ready` flag would short-circuit.
                delete m.target.dataset[PROCESSED];
                applySmooth(m.target);
            }
        }
    });
    observer.observe(document.body, {
        attributeFilter: ['src'],
        attributes: true,
        childList: true,
        subtree: true,
    });
}

export default {
    install() {
        if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;
        if (document.body) {
            startObserving();
        } else {
            document.addEventListener('DOMContentLoaded', startObserving, { once: true });
        }
    },
};
